/**
 * Voice Monitor Window
 * Integrated window for voice chat monitoring and live transcription.
 *
 * It stays live while the team's card it was opened from stays open: a
 * FloatingWindow (components/floating-window.tsx), the wide one, over every
 * dialog's scrim and not modal — it takes neither the focus nor Escape from
 * the card, as the game client's call window beside a dialog.
 *
 * A room it cannot reach, or that drops, it says so, in the words the private
 * call says it (room-failure.ts), where it said "Connecting..." for ever. It
 * logs nothing a student says, nor who they are.
 */

import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiveKitRoom, RoomAudioRenderer, useRemoteParticipants, useRoomContext } from '@livekit/components-react';
import type {
  DisconnectReason,
  RemoteParticipant,
  RemoteTrackPublication,
  TranscriptionSegment,
} from 'livekit-client';
import { useLiveTranscriptionStore } from './stores/live-transcription';
import { roomFailure, roomLinkAfter, type RoomLink } from './room-failure';
import monitor from './voice-monitor-window.module.css';
import { FloatingWindow } from '../../components/floating-window';
import { WorldAndTeam } from './world-and-team';
import { RoomStatus } from './room-status';
import { HEARING_EVERYONE, hearingAfter, mutedIn, useRemoteVolumes } from './remote-volumes';
import { isAgentIdentity, isModeratorIdentity } from './room-identities';
import { nameAsSaid } from './said-names';

interface VoiceMonitorWindowProps {
  token: string;
  serverUrl: string;
  worldName: string;
  cohortName: string;
  onClose: () => void;
}

/**
 * Transcription Controller - Receives standard LiveKit transcription streams
 * Uses RoomEvent.TranscriptionReceived (official pattern from LiveKit examples)
 */
function TranscriptionController() {
  const { t } = useTranslation();
  const room = useRoomContext();
  const addTranscript = useLiveTranscriptionStore((s) => s.addTranscript);

  useEffect(() => {
    if (!room) return;

    const handleTranscriptionReceived = (
      segments: TranscriptionSegment[],
      participant?: RemoteParticipant,
      _publication?: RemoteTrackPublication,
    ) => {
      segments.forEach((segment) => {
        // Only process final transcriptions
        if (!segment.final) return;

        const text = segment.text;
        if (!text || !text.trim()) return;

        // Get participant info: one the room cannot name is said so, in the moderator's language.
        const participantIdentity = participant?.identity || 'unknown';
        const participantName = participant?.name || participant?.identity || t('players.unknownSpeaker');

        // Skip transcriptions from agents
        if (isAgentIdentity(participantIdentity)) return;

        // A moderator's name marked so, in the moderator's language (nameAsSaid).
        const displayName = nameAsSaid(t, participantName, isModeratorIdentity(participantIdentity));

        // Add transcript to store with translated moderator indicator
        addTranscript({
          id: segment.id || crypto.randomUUID(),
          speaker_identity: participantIdentity,
          speaker_name: displayName,
          text: text.trim(),
          language: segment.language,
          timestamp: new Date().toISOString(),
        });
      });
    };

    // The event exists at runtime but is not in livekit-client's typed event
    // map, so the listener has to be registered through a widened `on`. Kept as
    // a single named cast rather than `as any` at both call sites.
    const roomEvents = room as unknown as {
      on(event: string, cb: typeof handleTranscriptionReceived): void;
      off(event: string, cb: typeof handleTranscriptionReceived): void;
    };
    roomEvents.on('transcriptionReceived', handleTranscriptionReceived);

    return () => {
      roomEvents.off('transcriptionReceived', handleTranscriptionReceived);
    };
  }, [room, addTranscript, t]);

  return null;
}

/**
 * Who is in the monitor's room, as LiveKit tells it (useRemoteParticipants):
 * the list renders again as anyone joins, leaves or starts speaking, and the
 * transcriber is not listed. It kept a copy of its own, filled by an effect
 * that read the room's state in the render, which LiveKit renders nothing
 * again for: the copy filled because something else rendered the list as the
 * room connected — the window, or its volume loop's tracks. Whom the
 * moderator hears is hearingAfter's (remote-volumes.ts): the copy's effect
 * muted everyone again whenever anyone came or went while no one was heard.
 */
function ParticipantList() {
  const { t } = useTranslation();
  const participants = useRemoteParticipants().filter((p) => !isAgentIdentity(p.identity));
  const [hearing, turn] = useReducer(hearingAfter, HEARING_EVERYONE);

  // A participant's name, a moderator's marked so (nameAsSaid).
  const getDisplayName = (participant: RemoteParticipant): string =>
    nameAsSaid(t, participant.name || participant.identity, isModeratorIdentity(participant.identity));

  // Each participant heard or muted on this browser, as the moderator chose:
  // a new answer only when the choice changes.
  const mutedFor = useCallback((identity: string) => mutedIn(hearing, identity), [hearing]);
  useRemoteVolumes(mutedFor);

  return (
    <div className={monitor.voiceMonitorParticipants}>
      <div className={monitor.voiceMonitorSectionHeader}>
        <h4>{t('voiceMonitor.participants')} ({participants.length})</h4>
        <button
          onClick={() => turn({ type: 'turnAll' })}
          className={`${monitor.voiceMonitorToggleBtn} ${hearing.allMuted ? monitor.muted : ''}`}
          title={hearing.allMuted ? t('voiceMonitor.unmuteAudio') : t('voiceMonitor.muteAudio')}
        >
          {hearing.allMuted ? '🔇' : '🔊'}
        </button>
      </div>
      <div className={monitor.voiceMonitorParticipantsList}>
        {participants.length === 0 ? (
          <p className={monitor.voiceMonitorEmpty}>{t('voiceMonitor.noParticipants')}</p>
        ) : (
          participants.map((p) => {
            const isMuted = mutedIn(hearing, p.identity);
            return (
              <div key={p.identity} className={monitor.voiceMonitorParticipant}>
                <span className={monitor.voiceMonitorParticipantName}>{getDisplayName(p)}</span>
                <div className={monitor.voiceMonitorParticipantActions}>
                  {p.isSpeaking && <span className={monitor.voiceMonitorSpeakingIndicator}>🎤</span>}
                  <button
                    onClick={() => turn({ type: 'turnOne', identity: p.identity })}
                    className={`${monitor.voiceMonitorParticipantMuteBtn} ${isMuted ? monitor.muted : ''}`}
                    title={isMuted ? t('voiceMonitor.unmuteParticipant') : t('voiceMonitor.muteParticipant')}
                  >
                    {isMuted ? '🔇' : '🔊'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * Transcription Panel Component
 * Memoized to prevent unnecessary re-renders
 */
const TranscriptionPanel = React.memo(function TranscriptionPanel({ 
  visible, 
  onToggle 
}: { 
  visible: boolean;
  onToggle: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { transcripts, clearTranscripts } = useLiveTranscriptionStore();
  const contentRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (contentRef.current && visible) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [transcripts, visible]);
  
  // A time that cannot be read shows nothing: new Date reads an unparseable
  // string as an invalid date, which it does not throw for.
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString(i18n.language, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  
  return (
    <div className={monitor.voiceMonitorTranscription}>
      <div className={monitor.voiceMonitorSectionHeader}>
        <h4>{t('voiceMonitor.transcription')}</h4>
        <div className={monitor.voiceMonitorSectionHeaderActions}>
          <button
            onClick={onToggle}
            className={`${monitor.voiceMonitorToggleBtn} ${!visible ? monitor.disabled : ''}`}
            title={visible ? t('voiceMonitor.hideTranscription') : t('voiceMonitor.showTranscription')}
          >
            {visible ? '👁️' : '👁️‍🗨️'}
          </button>
          <button
            onClick={clearTranscripts}
            className={monitor.voiceMonitorClearBtn}
            title={t('voiceMonitor.clearTranscripts')}
          >
            🗑️
          </button>
        </div>
      </div>
      
      {visible && (
        <>
          <div ref={contentRef} className={monitor.voiceMonitorTranscriptionContent}>
            {transcripts.length === 0 ? (
              <p className={monitor.voiceMonitorEmpty}>{t('voiceMonitor.noTranscripts')}</p>
            ) : (
              <div className={monitor.voiceMonitorTranscriptionList}>
                {transcripts.map((transcript) => (
                  <div key={transcript.id} className={monitor.voiceMonitorTranscriptEntry}>
                    <div className={monitor.voiceMonitorTranscriptHeader}>
                      <span className={monitor.voiceMonitorTranscriptSpeaker}>
                        {transcript.speaker_name}
                      </span>
                      <span className={monitor.voiceMonitorTranscriptTimestamp}>
                        {formatTimestamp(transcript.timestamp)}
                      </span>
                    </div>
                    <div className={monitor.voiceMonitorTranscriptText}>
                      {transcript.text}
                      {transcript.language && (
                        <span className={monitor.voiceMonitorTranscriptLanguage}>
                          [{transcript.language}]
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className={monitor.voiceMonitorTranscriptionFooter}>
            <span className={monitor.voiceMonitorTranscriptCount}>
              {t('voiceMonitor.transcriptCount', { count: transcripts.length })}
            </span>
          </div>
        </>
      )}
    </div>
  );
});

/** Where the monitor's room stands as the window opens. */
const ROOM_OPENING: RoomLink = { connectionState: 'connecting', failure: null };

/**
 * Main Voice Monitor Window Component
 */
export function VoiceMonitorWindow({ token, serverUrl, worldName, cohortName, onClose }: VoiceMonitorWindowProps) {
  const { t } = useTranslation();
  const [transcriptionVisible, setTranscriptionVisible] = useState(true);
  // The transcript panel draws again only when what it shows changes
  // (React.memo): its toggle is the same function for the window's whole life,
  // where one written in the render was a new one at every render of the window.
  const toggleTranscription = useCallback(() => setTranscriptionVisible((visible) => !visible), []);

  // Where its room stands, as LiveKit tells (roomLinkAfter, room-failure.ts).
  // Each callback is the same one for the window's whole life: LiveKitRoom
  // connects again whenever the onError it is given changes, and one written
  // in the render connected again at every toggle in the window — LiveKit
  // answered "already connected to room", which the window once hid by
  // wrapping console.log.
  const [link, tell] = useReducer(roomLinkAfter, ROOM_OPENING);
  const onRoomConnected = useCallback(() => tell({ type: 'connected' }), []);
  const onRoomDisconnected = useCallback((reason?: DisconnectReason) => tell({ type: 'disconnected', reason }), []);
  const onRoomError = useCallback((error: Error) => {
    console.error('[VoiceMonitor] Error:', error);
    const failure = roomFailure(error);
    if (failure) tell({ type: 'failed', failure });
  }, []);
  const failed = link.connectionState === 'error';
  // The transcript is the window's: emptied as it opens and as it closes,
  // whichever way it closes — its own close, the voice session opening another
  // team's monitor in its place, or the screen going. The session cleared it
  // too, on each of its paths.
  const clearTranscripts = useLiveTranscriptionStore((s) => s.clearTranscripts);
  useEffect(() => {
    clearTranscripts();
    return clearTranscripts;
  }, [clearTranscripts]);

  return (
    <FloatingWindow
      title={t('voiceMonitor.title')}
      closeLabel={t('common.close')}
      moveLabel={t('common.moveWindow')}
      onClose={onClose}
      size="wide"
      initialPosition={{ x: 340, y: 20 }}
    >
      <div className={monitor.voiceMonitorContent}>
        <WorldAndTeam world={worldName} team={cohortName} size="wide" />
        
        <LiveKitRoom
          token={token}
          serverUrl={serverUrl}
          connect={true}
          audio={false}  // Don't capture audio from moderator
          video={false}
          onConnected={onRoomConnected}
          onDisconnected={onRoomDisconnected}
          onError={onRoomError}
          options={{
            // Don't try to publish any tracks
            publishDefaults: {
              dtx: false,
              simulcast: false,
            },
            // Prioritize data channel for transcription
            adaptiveStream: false,
            dynacast: false,
          }}
        >
          <TranscriptionController />
          {/* Always render to keep connection */}
          <RoomAudioRenderer />
          
          <RoomStatus {...link} size="wide" />
          
          {/* A room that failed has no one in it to list or mute — the status
              above says why — as a failed private call lists no one. */}
          {!failed && <ParticipantList />}
        </LiveKitRoom>
        
        {/* TranscriptionPanel OUTSIDE LiveKitRoom to avoid render conflicts */}
        <TranscriptionPanel visible={transcriptionVisible} onToggle={toggleTranscription} />
      </div>
    </FloatingWindow>
  );
}
